import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import { useAuth } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import { isSysAdminUser } from '@/utils/isSysAdminUser';

export type DeviceObsStatus = 'open' | 'done' | 'cancelled';

export interface DeviceObsRow {
  id: number;
  imei: string;
  registration_number: string | null;
  reason: string;
  note: string;
  status: DeviceObsStatus;
  created_by_name: string | null;
  created_at: string;
}

interface DeviceObsListPanelProps {
  selectedImei?: string | null;
  selectedReg?: string | null;
  onOpenImeisChange?: (imeis: Set<string>) => void;
}

function formatLocal(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const DeviceObsListPanel: React.FC<DeviceObsListPanelProps> = ({
  selectedImei,
  selectedReg,
  onOpenImeisChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canUse = isSysAdminUser(user);
  const [rows, setRows] = useState<DeviceObsRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!canUse) return;
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: DeviceObsRow[] }>(
        '/api/device-management/obs-list?status=open'
      );
      const next = res.data?.data ?? [];
      setRows(next);
      onOpenImeisChange?.(new Set(next.map((r) => r.imei)));
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast.error(message || t('deviceManagement.obsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [canUse, onOpenImeisChange, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectedOpen = useMemo(
    () => rows.find((r) => selectedImei && r.imei === selectedImei) ?? null,
    [rows, selectedImei]
  );

  const addSelected = async () => {
    if (!selectedImei) return;
    setSaving(true);
    try {
      await api.post('/api/device-management/obs-list', {
        imei: selectedImei,
        registrationNumber: selectedReg,
        note: note.trim() || t('deviceManagement.obsDefaultNote'),
      });
      setNote('');
      toast.success(t('deviceManagement.obsAdded'));
      await refresh();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast.error(message || t('deviceManagement.obsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const resolve = async (id: number, status: 'done' | 'cancelled') => {
    setSaving(true);
    try {
      await api.patch(`/api/device-management/obs-list/${id}`, { status });
      toast.success(
        status === 'done' ? t('deviceManagement.obsMarkedDone') : t('deviceManagement.obsCancelled')
      );
      await refresh();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast.error(message || t('deviceManagement.obsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!canUse) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <ExclamationTriangleIcon className="w-4 h-4 text-amber-700 dark:text-amber-300" />
        <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
          {t('deviceManagement.obsTitle')}
        </h3>
        <span className="text-xs text-amber-800 dark:text-amber-200">
          {loading ? '…' : t('deviceManagement.obsOpenCount').replace('{count}', String(rows.length))}
        </span>
      </div>
      <p className="text-xs text-amber-800 dark:text-amber-200 mb-2">
        {t('deviceManagement.obsHint')}
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-textSecondary">{t('deviceManagement.obsEmpty')}</p>
      ) : (
        <ul className="space-y-2 max-h-48 overflow-y-auto">
          {rows.map((row) => (
            <li key={row.id} className="text-xs bg-white/70 dark:bg-black/20 rounded p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-medium text-textDefault">
                  {row.registration_number || row.imei}
                </span>
                <span className="text-textTertiary">{formatLocal(row.created_at)}</span>
              </div>
              {row.registration_number && (
                <div className="font-mono text-textSecondary">{row.imei}</div>
              )}
              <p className="mt-1 text-textDefault whitespace-pre-wrap">{row.note}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void resolve(row.id, 'done')}
                  className="px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200"
                >
                  {t('deviceManagement.obsMarkDone')}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void resolve(row.id, 'cancelled')}
                  className="px-2 py-1 rounded bg-bgBase text-textSecondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {selectedImei && !selectedOpen && (
        <div className="mt-3 pt-2 border-t border-amber-200 dark:border-amber-800">
          <label className="block text-xs text-amber-900 dark:text-amber-100 mb-1">
            {t('deviceManagement.obsAddSelected')}
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full text-xs px-2 py-1 border border-borderDefault rounded bg-bgBase text-textDefault"
            placeholder={t('deviceManagement.obsNotePlaceholder')}
          />
          <button
            type="button"
            disabled={saving}
            onClick={() => void addSelected()}
            className="mt-1 px-2 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-700"
          >
            {t('deviceManagement.obsAdd')}
          </button>
        </div>
      )}
    </div>
  );
};

export default DeviceObsListPanel;
